// ============================
// src/Pages/Usuario/agenda.jsx
// ============================

/* eslint-disable import/first */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../../firebase/config";
import { useAuth } from "../../context/AuthContext";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
  query,
  where,
  onSnapshot,
  getDocs,
  updateDoc,
  increment,
  Timestamp,
} from "firebase/firestore";
import regionesYComunas from "../../utils/regionesComunas";
import useServicios from "../../hooks/useServicios";

// ================= Componentes externos =================
import SlotBtn from "./Agenda/components/SlotBtn";
import BotonDinamico from "./Agenda/components/BotonDinamico";
import DatosPaciente from "./Agenda/components/DatosPaciente";
import ServicioSelector from "./Agenda/components/ServicioSelector";
import HorariosGrid from "./Agenda/components/HorariosGrid";
import ResumenReserva from "./Agenda/components/ResumenReserva";
import ModalConfirmacion from "./Agenda/components/ModalConfirmacion";
import ModalPago from "./Agenda/components/ModalPago";
import Paso from "./Agenda/components/Paso";
import FechaSelector from "./Agenda/components/FechaSelector";
import useAgendaForm from "./Agenda/hooks/useAgendaForm";
import useHorarios from "./Agenda/hooks/useHorarios";
import { getReglaServicio } from "./Agenda/config/reglasServicios.js";
import usePlanesUsuario from "../../hooks/usePlanesUsuario";
import { obtenerSesionesEntrenamiento1x } from "../../utils/planesEntrenamiento1x";
import { buscarServicioDePlan } from "../../utils/planServicioMatch";

// ===================== Utils =====================
const clp = (n) =>
  typeof n === "number" ? `$${n.toLocaleString("es-CL")}` : "—";

// ===================== Utils extra (cupón por servicio) =====================
const normKey = (s = "") =>
  s
    .toString()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // quita tildes
    .trim();

const getServicioIdDesdeTitulo = (titulo, servicios = []) => {
  const t = normKey(titulo);
  const hit = (servicios || []).find((s) => normKey(s.title) === t);
  return hit?.id || "";
};

const makeVarianteToken = (servicioId, varianteKey) =>
  `${servicioId}::${normKey(varianteKey)}`;


const STEP_MIN = 30; 

const PRECIO_PRIMERA_SESION_PLAN = 30000;

const getClavePrimeraSesion = (categoria = "", variante = "") => {
  const c = (categoria || "").toLowerCase();
  const v = (variante || "").toLowerCase();

  if (c.includes("masoter") || v.includes("masoter")) return "masoterapia";
  if (c.includes("masaj") || v.includes("masaj")) return "masajes";
  if (c.includes("reco")) return "recovery";
  if (c.includes("entren")) return "entrenamiento";
  if (c.includes("kine")) return "kinesiologia";

  return null;
};

const calcularPrecioSesionPosteriorPlan = (precioTotal, totalSesiones) => {
  if (totalSesiones <= 1) return precioTotal || 0;

  const restante = Math.max(0, (precioTotal || 0) - PRECIO_PRIMERA_SESION_PLAN);
  return Math.round(restante / (totalSesiones - 1));
};


const fmtYMD = (d) => d.toISOString().slice(0, 10);
const parseYMD = (str) => {
  const [y, m, d] = str.split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1, 0, 0, 0, 0);
};


const HHmmToDate = (ymd, hhmm) => {
  const d = parseYMD(ymd);
  const [h, m] = hhmm.split(":").map(Number);
  d.setHours(h || 0, m || 0, 0, 0);
  return d;
};

const addMinutes = (date, minutes) =>
  new Date(date.getTime() + minutes * 60000);

const toHHmm = (d) =>
  `${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes()
  ).padStart(2, "0")}`;

const overlaps = (aStart, aEnd, bStart, bEnd) =>
  aStart < bEnd && bStart < aEnd;

const isAfterCutoffForTomorrow = (targetYMD) => {
  const now = new Date();
  const tomorrow = addMinutes(
    new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0),
    24 * 60
  );
  const isTargetTomorrow = fmtYMD(tomorrow) === targetYMD;
  if (!isTargetTomorrow) return false;
  const cutoff = new Date(now);
  cutoff.setHours(14, 0, 0, 0);
  return now.getTime() >= cutoff.getTime();
};

const buildSlots70 = (ymd, dayStart = "07:30", dayEnd = "20:00") => {
  if (!ymd) return [];

  const start = HHmmToDate(ymd, dayStart);
  const end = HHmmToDate(ymd, dayEnd);

  const out = [];
  let cur = new Date(start);

  while (cur.getTime() <= end.getTime()) {
    out.push(toHHmm(cur));
    cur = addMinutes(cur, STEP_MIN);
  }

  return out;
};

// ===================== Estilos para React Select =====================
const selectStyles = {
  control: (base) => ({
    ...base,
    border: "1px solid #dbeafe",
    borderRadius: "12px",
    padding: "4px 8px",
    backgroundColor: "rgba(255, 255, 255, 0.9)",
    "&:hover": {
      borderColor: "#3A6AFF",
    },
  }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    container: (base) => ({ ...base, zIndex: 9999 }),
  menu: (base) => ({
    ...base,
    borderRadius: "12px",
    overflow: "hidden",
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? "#3A6AFF" : state.isFocused ? "#dbeafe" : "white",
    color: state.isSelected ? "white" : "#1e3a8a",
    "&:active": {
      backgroundColor: "#3A6AFF",
      color: "white",
    },
  }),
};

const sesionesPorServicioFromStrings = (categoria, variante, meta = null) => {
  const lower = (variante || "").toLowerCase();

  // 🔥 CASO PLAN CON METADATA DIRECTA
  if (meta?.es_plan) {
    if (typeof meta.total_sesiones === "number") return meta.total_sesiones;

    const semanas = (meta.duracion_meses || 1) * 4;
    const porSemana = meta.sesiones_por_semana || 1;
    return semanas * porSemana;
  }

  // SOLO los tres IDs antiguos mal detectados; respetar un override explícito.
  if (meta?.es_plan !== false) {
    const sesiones1x = obtenerSesionesEntrenamiento1x(categoria, meta?.id);
    if (sesiones1x !== null) return sesiones1x;
  }

  // 🔥 DETECCIÓN POR NOMBRE (Planes: mensual, trimestral, semestral)
  const match = lower.match(/(\d+)\s*veces\s*x\s*sem/);
  if (match) {
    const veces = parseInt(match[1], 10);

    if (/mensual/i.test(lower)) return veces * 4;
    if (/trimestral/i.test(lower)) return veces * 12;
    if (/semestral/i.test(lower)) return veces * 24;
  }

  // Packs de kine: "5 sesiones", "10 sesiones", etc.
  const m = lower.match(/(\d+)\s*ses/);
  if (m) return parseInt(m[1], 10);

  return 1;
};

// ===================== Modalidad helpers =====================
// Estado UI/reglas: "grupo" | "privada"
// DB: "grupal" | "privada"
const normModalidadUI = (m) => {
  const x = (m || "").toString().toLowerCase().trim();
  if (x.includes("priv")) return "privada";
  if (x.includes("gru") || x === "grupo" || x === "grupal") return "grupo";
  return null;
};

const normModalidadDB = (m) => {
  const ui = normModalidadUI(m);
  if (ui === "privada") return "privada";
  if (ui === "grupo") return "grupal";
  return null;
};



// CUPOS POR SERVICIO
const cuposPorServicio = (categoria, variante, modalidadKine) => {
  const cat = (categoria || "").toLowerCase();
  const v = (variante || "").toLowerCase();

  // Kinesiología
  if (cat === "kinesiología") {
    if (modalidadKine === "grupo") return 2;
    if (modalidadKine === "privada") return 1;

    // Caso packs: 1 sesión de kine pero no escogieron modalidad (edge case)
    if (v.includes("1 sesión")) return 1;
  }

  // Entrenamiento deportivo
  if (cat === "entrenamiento") return 2;

  // Recovery
  if (cat === "recovery") return 1;

  // Masajes
  if (cat === "masajes") return 1;

  return 1;
};

// Helpers para sesiones válidas
const cuentaComoRealizada = (r) =>
  r.estadoSesion === "completada" || r.estadoSesion === "realizada";

const estadoCuentaParaProgreso = (r) =>
  ["pendiente", "aprobada", "realizada"].includes(r.estado);

function getEstadoPlan(plan) {
  if (!plan) return "activo";

  if (plan.estado_plan === "anulado") {
    return "anulado";
  }

  if (plan.estado_plan === "finalizado") {
    return "finalizado";
  }

  const usadas = Number(plan.sesiones_usadas || 0);
  const totales = Number(plan.sesiones_totales || 0);

  if (totales > 0 && usadas >= totales) {
    return "completado";
  }

  return "activo";
}

function puedeReservarPlan(plan) {
  const estado = getEstadoPlan(plan);

  return (
    estado !== "finalizado" &&
    estado !== "anulado" &&
    estado !== "completado"
  );
}

const normalizarCategoriaReserva = (rawCat, rawMod = "") => {
  const c = (rawCat || "").toLowerCase();
  const m = (rawMod || "").toLowerCase();

  // ====== KINESIOLOGÍA ======
  if (c.includes("kine")) {
    if (m.includes("priv")) return "kinesiologia_privada";
    if (m.includes("grup") || m.includes("multi")) return "kinesiologia_grupal";
    return "kinesiologia_privada"; // fallback seguro
  }

  // ====== ENTRENAMIENTO ======
  if (c.includes("entre")) return "entrenamiento";

  // ====== RECOVERY ======
  if (c.includes("reco")) return "recovery";

  // ====== MASAJES ======
  if (c.includes("masa")) return "masajes";

  return "otro";
};


// Servicios que bloquean completamente el bloque
const esExclusivo = (cat, mod) => {
  const c = (cat || "").toLowerCase();
  const m = (mod || "").toLowerCase();

  // Masajes = exclusivo
  if (c.includes("masaje")) return true;

  // Recovery = exclusivo
  if (c.includes("recovery")) return true;

  // Kinesiología privada = exclusivo
  if (c.includes("kinesi") && m.includes("priv")) return true;

  return false;
};


// ===================== Componente Principal =====================
const Agenda = () => {
const { user: usuario } = useAuth();

  const { planes, loadingPlanes } = usePlanesUsuario(usuario?.uid);

const planesActivos = useMemo(() => {
  const arr = Object.entries(planes || {}).map(([planId, p]) => ({
    planId,
    ...p,
  }));

  return arr.filter((p) => {
    const total = Number(p.sesiones_totales || 0);
    const reservadas = Number(p.sesiones_reservadas || 0);

    return (
      total > 0 &&
      reservadas < total &&
      puedeReservarPlan(p)
    );
  });
}, [planes]);


  const {
  form,
  setForm,
  comunasFiltradas,
  mostrarDatos,
  setMostrarDatos,
  handleChange,
  crearCuenta,
  handleGoogleSignup,
  faltantes,
} = useAgendaForm(usuario, regionesYComunas);

  const { servicios } = useServicios();
  const [aceptaTerminos, setAceptaTerminos] = useState(false);

  const getPreciosKine1Sesion = () => {
  const serv = servicios.find(s => s.id === "kinesiologia");
  if (!serv) return { grupo: 28000, privada: 32000 };

  const v1 = serv.variantes.find(v => v.id === "kinesiologia-1");
  if (!v1) return { grupo: 28000, privada: 32000 };

  return {
    grupo: v1.precio_grupo || v1.precio || 28000,
    privada: v1.precio_privada || v1.precio || 32000,
  };
};

  const [planEntrada, setPlanEntrada] = useState(null);
  const [modalidadKine, setModalidadKine] = useState(null);
  const [configPago, setConfigPago] = useState(null);

  const [primerasSesionesDB, setPrimerasSesionesDB] = useState({});

useEffect(() => {
  if (!usuario?.email) return;

  const ref = doc(db, "usuarios", usuario.email.toLowerCase());
  return onSnapshot(ref, (snap) => {
    const data = snap.data() || {};
    setPrimerasSesionesDB(data.primerasSesiones || {});
  });
}, [usuario?.email]);

  const isDiaPermitidoMasoterapia = (dateObj) => {
  const d = dateObj.getDay();
  return d === 1 || d === 2 || d === 4 || d === 5;
  };

  const [modalAbierto, setModalAbierto] = useState(false);
  const [pasoPago, setPasoPago] = useState(1);
  const [opcionPago, setOpcionPago] = useState(null);
  const [montoAPagarActual, setMontoAPagarActual] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [resumenReserva, setResumenReserva] = useState(null);
  const [cupon, setCupon] = useState("");
  const [cuponInfo, setCuponInfo] = useState(null);
  const [mensajeCupon, setMensajeCupon] = useState("");
  const [cuponLoading, setCuponLoading] = useState(false);
  const [mostrarConfirmacion, setMostrarConfirmacion] = useState(false);
  const [formTransferencia, setFormTransferencia] = useState({
    comprobanteFile: null,
  });
  const [copiado, setCopiado] = useState(false);

  const unsubReservasRef = useRef(null);
  const unsubBreaksRef = useRef(null);

const isMasajesMitadSemana = useMemo(() => {
  const categoria = form.servicio?.value || "";
  return categoria === "Masajes Mitad de Semana";
}, [form.servicio]);

const isMasoterapia = useMemo(() => {
  const categoria = form.servicio?.value || "";
  return categoria === "Masoterapia";
}, [form.servicio]);


const SERVICIOS_DYNAMIC_FLAT = useMemo(() => {
  if (!servicios || servicios.length === 0) return [];

  const out = [];

  servicios
    .filter((s) => s.activo)
    .forEach((s) => {
      const variantes = s.variantes;

      // Caso inválido o texto tipo "Próximamente"
      if (!variantes || typeof variantes === "string") return;

      // ------------------------------
      // CASO 1: variantes = ARRAY
      // ------------------------------
      if (Array.isArray(variantes)) {
        variantes.forEach((v) => {
          out.push({
            ...v, // ← incluye la metadata existente
            categoria: s.title,
            variante: v.nombre,
            display: `${v.nombre}: $${v.precio?.toLocaleString("es-CL")}`,
          });
        });
        return;
      }

      // ------------------------------
      // CASO 2: variantes = OBJETO (planes)
      // ------------------------------
      if (typeof variantes === "object") {
        Object.entries(variantes).forEach(([nivel, lista]) => {
          (lista || []).forEach((p) => {
            out.push({
              ...p, // ← incluye es_plan, sesiones_por_semana, duracion_meses, total_sesiones
              categoria: s.title,
              nivel,
              variante: p.nombre,
              display: `${p.nombre}: $${p.precio?.toLocaleString("es-CL")}`,
            });
          });
        });
      }
    });

  return out;
}, [servicios]);


// =====================================
// 2) Opciones para Select (react-select)
// =====================================

// Servicio = categoría (Kine, Masajes, etc.)
const opcionesServicios = useMemo(() => {
  const categoriasUnicas = Array.from(
    new Set(SERVICIOS_DYNAMIC_FLAT.map((s) => s.categoria))
  );

  return categoriasUnicas.map((cat) => ({
    value: cat,
    label: cat,
  }));
}, [SERVICIOS_DYNAMIC_FLAT]);

const opcionesServiciosAgrupadas = useMemo(() => {
  const opcionesPlanes = (planesActivos || []).map((p) => ({
    value: `plan::${p.planId}`,
    label: `${p.servicio_categoria || p.categoria || p.servicio} — ${p.servicio_variante || p.variante}`,
    isPlan: true,
    planData: p, 
  }));

  const opcionesServiciosDisponibles = opcionesServicios;

  if (opcionesPlanes.length === 0) return opcionesServiciosDisponibles;

  return [
    { label: "Plan(es) Activos", options: opcionesPlanes },
    { label: "Servicios Disponibles", options: opcionesServiciosDisponibles },
  ];
}, [planesActivos, opcionesServicios]);


const opcionesVariantes = useMemo(() => {
  if (!form.servicio) return [];

  return SERVICIOS_DYNAMIC_FLAT
    .filter((s) => s.categoria === form.servicio.value)
    .map((s) => {
      const esIsapre = /isapre/i.test(s.variante);
      const esFonasa = /fonasa/i.test(s.variante);

      return {
        value: s.variante,
        label: s.display,
        data: {
          ...s,

          // Valor grupal SIEMPRE existente
          precio_grupo:
            typeof s.precio_grupo === "number"
              ? s.precio_grupo
              : s.precio,

          // Valor privada:
          precio_privada:
            typeof s.precio_privada === "number"
              ? s.precio_privada
              : esIsapre
              ? s.precio + 4000     // regla privada Isapre
              : esFonasa
              ? null                // Fonasa no tiene privada
              : null,
        },
      };
    });
}, [form.servicio, SERVICIOS_DYNAMIC_FLAT]);

const preseleccionarServicioDesdePlan = (plan) => {
  if (!plan || SERVICIOS_DYNAMIC_FLAT.length === 0) return;

  // Buscar por familia + variante + periodo + frecuencia + pareja, no por el
  // nombre exacto que el plan tenía cuando fue adquirido.
  const encontrado = buscarServicioDePlan(plan, SERVICIOS_DYNAMIC_FLAT);
  const categoriaPlanBase = plan.servicio_categoria || plan.categoria || plan.servicio || "Servicio";
  const categoriaPlan = plan.es_pareja && !/pareja/i.test(categoriaPlanBase)
    ? "Entrenamiento en Pareja" : categoriaPlanBase;
  const variantePlanBase = plan.servicio_variante || plan.variante || "Sesión del plan";
  const variantePlan = plan.es_pareja && !/pareja/i.test(variantePlanBase)
    ? `${variantePlanBase} (Pareja)` : variantePlanBase;

  // Si la variante ya no está en el catálogo (plan histórico), igual permitir
  // utilizar el plan comprado sin inventar su valor ni crear otro plan.
  const categoria = encontrado?.categoria || categoriaPlan;
  const variante = encontrado?.variante || variantePlan;
  const servicioOpt = opcionesServicios.find((o) => o.value === categoria) || {
    value: categoria, label: categoria,
  };

  const esIsapre = /isapre/i.test(variante);
  const esFonasa = /fonasa/i.test(variante);
  const precioBase = Number(encontrado?.precio ?? plan.precio_por_sesion ?? plan.precio_total ?? 0);
  const metadata = encontrado
    ? {
        ...encontrado,
        precio_grupo: typeof encontrado.precio_grupo === "number" ? encontrado.precio_grupo : precioBase,
        precio_privada: typeof encontrado.precio_privada === "number"
          ? encontrado.precio_privada : esIsapre ? precioBase + 4000 : esFonasa ? null : null,
      }
    : {
        categoria, variante, nombre: variante, precio: precioBase,
        es_plan: true, total_sesiones: Number(plan.sesiones_totales || 0),
        modalidadesPermitidas: plan.modalidad
          ? { privada: /priv/i.test(plan.modalidad), grupal: /gru/i.test(plan.modalidad) }
          : undefined,
      };

  setForm((prev) => ({
    ...prev,
    servicio: servicioOpt,
    variante: { value: variante, label: variante, data: metadata },
    // No arrastrar un horario escogido para otro servicio.
    hora: null,
    bloqueSeleccionado: null,
  }));
};

// Si el plan llegó antes que editar_servicios, resolver la preselección cuando
// el catálogo termine de cargar (sin cambiar el plan elegido por la persona).
useEffect(() => {
  if (!planEntrada || SERVICIOS_DYNAMIC_FLAT.length === 0) return;
  if (form.servicio && form.variante) return;
  preseleccionarServicioDesdePlan(planEntrada);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [planEntrada, SERVICIOS_DYNAMIC_FLAT, form.servicio, form.variante]);

useEffect(() => {
  if (!usuario) return;
  if (planEntrada) return;
  if (loadingPlanes) return;
  if (form?.servicio) return;

  if (planesActivos.length === 1) {
    const p = planesActivos[0];
    setPlanEntrada(p);

    if (p?.modalidad) setModalidadKine(normModalidadUI(p.modalidad));

    preseleccionarServicioDesdePlan(p);
  }
}, [usuario, loadingPlanes, planesActivos, planEntrada, form?.servicio]);


const parsed = useMemo(() => {
  if (!form.servicio || !form.variante) return {};

  const categoriaRaw = form.servicio.value;
  const data = form.variante.data;

  let categoriaNorm = categoriaRaw;
  if (/kine/i.test(categoriaNorm)) categoriaNorm = "Kinesiología";
  if (/fonasa/i.test(data?.nombre || "") || /fonasa/i.test(data?.variante || "")) {
  setModalidadKine("grupo");       // siempre grupal
}
  else if (/entre/i.test(categoriaNorm)) categoriaNorm = "Entrenamiento";
  else if (/reco/i.test(categoriaNorm)) categoriaNorm = "Recovery";
  else if (/masa/i.test(categoriaNorm)) categoriaNorm = "Masajes";

  // ==========================
  // 🔥 DETECCIÓN UNIVERSAL DE PLAN
  // ==========================
  const esPlan =
    data?.es_plan === true ||
    (typeof data?.total_sesiones === "number" && data.total_sesiones > 1) ||
    /mensual|trimestral|semestral|plan|x sem/i.test(data?.nombre || "") ||
    /mensual|trimestral|semestral|plan|x sem/i.test(data?.variante || "");

  return {
    categoria: categoriaNorm,
    variante: data?.nombre || form.variante.value,
    precio: data?.precio || 0,
    precio_grupo: data?.precio_grupo || null,
    precio_privada: data?.precio_privada || null,
    meta: data,
    esPlan, // 🔥 NECESARIO
  };
}, [form.servicio, form.variante]);

useEffect(() => {
  const mp = form?.variante?.data?.modalidadesPermitidas;
  if (!mp) return;

  if (mp.grupal && !mp.privada) {
    setModalidadKine("grupo");
  } else if (mp.privada && !mp.grupal) {
    setModalidadKine("privada");
  }
}, [form?.variante]);


 const esEntrenamientoPareja =
  parsed.categoria === "Entrenamiento" &&
  parsed.variante?.toLowerCase().includes("pareja");
  // Definir si es un pack nuevo
const meta = form?.variante?.data || null;

const totalSesionesEstimadas =
  sesionesPorServicioFromStrings(parsed.categoria, parsed.variante, meta);

  const esKine = /kine/i.test(parsed.categoria);

  const clavePrimeraSesion = getClavePrimeraSesion(parsed.categoria, parsed.variante);

  let esPrimeraSesionReal = false;

  if (clavePrimeraSesion) {
    if (planEntrada) {
      // Primera sesión del plan activo
      esPrimeraSesionReal =
        (planEntrada.sesiones_realizadas ?? 0) === 0 &&
        (planEntrada.sesiones_reservadas ?? 0) === 0;
    } else {
      // Primera sesión histórica del usuario para ese servicio
      esPrimeraSesionReal = primerasSesionesDB[clavePrimeraSesion] !== true;
    }
  }



  // --- Detectar si la variante seleccionada es '1 sesión' ---
  const esVariante1Sesion =
    /1\s*ses/i.test(parsed.variante) &&
    totalSesionesEstimadas === 1 &&
    !planEntrada;

const nombreVar = (form?.variante?.data?.nombre || form?.variante?.value || "");
const esFonasa = /fonasa/i.test(nombreVar);

const modalidadNorm = (() => {
  const m = (modalidadKine || "").toString().toLowerCase();
  if (m.includes("priv")) return "privada";
  if (m.includes("gru")) return "grupal";
  return null;
})();


const varianteActual = form?.variante?.data || {};

const modalidadesPermitidas =
  varianteActual?.modalidadesPermitidas || {};

const modalidadParaReglas = (() => {

  // Primera sesión siempre privada
  if (esPrimeraSesionReal)
    return "privada";

  // Servicio sólo grupal
  if (
    modalidadesPermitidas.grupal === true &&
    modalidadesPermitidas.privada === false
  ) {
    return "grupal";
  }

  // Servicio sólo privado
  if (
    modalidadesPermitidas.privada === true &&
    modalidadesPermitidas.grupal === false
  ) {
    return "privada";
  }

  // Ambos permitidos → usar selección usuario
  return modalidadNorm;

})();

const reglaActual =
  getReglaServicio(
    parsed.categoria,
    parsed.variante,
    modalidadParaReglas,
    esPrimeraSesionReal
  ) || {};



  const duracionSesion = reglaActual.duracionMin || 60;

  // --- Usar lógica unificada:
  const esKine1Sesion = esKine && (esVariante1Sesion || esPrimeraSesionReal);

  const preciosKine1 = useMemo(() => getPreciosKine1Sesion(), [servicios]);

      const getPrecioFinalAntesDescuento = () => {
      const totalSesiones = sesionesPorServicioFromStrings(
        parsed.categoria,
        parsed.variante,
        meta
      );

      const esKineLocal = parsed.categoria === "Kinesiología";
      const esPackLocal = totalSesiones > 1;

      if (!esKineLocal) return Number(parsed.precio || 0);

      if (esPackLocal) {
        return Number(
          modalidadKine === "privada"
            ? parsed.precio_privada || parsed.precio || 0
            : parsed.precio_grupo || parsed.precio || 0
        );
      }

      return Number(
        modalidadKine === "privada"
          ? parsed.precio_privada || preciosKine1.privada || parsed.precio || 0
          : parsed.precio_grupo || preciosKine1.grupo || parsed.precio || 0
      );
    };

    const {
      horarios,
      estadoPorHora,
      ocupacionBloques,
      contadorPorSlot,
      effectiveBreaks,
      fechaError,
      minDate,
      minDateForService,
      primarySlot,
      setPrimarySlot,
      onPickPrimary,
    } = useHorarios({
      fechaSeleccionada: form.fecha,
      categoria: parsed.categoria,
      variante: parsed.variante,
      isMasoterapia,
      isMasajesMitadSemana,
      modalidadKine,
      planEntrada,
      esPrimeraSesionReal,
      reglaServicio: reglaActual, 
      publicAvailability: true,
    });


  const esPackNuevo = !planEntrada?.planId && totalSesionesEstimadas > 1;

useEffect(() => {
  if (opcionesServicios.length === 0) return;

  const raw = localStorage.getItem("servicioSeleccionado");
  const flujo = localStorage.getItem("flujoReserva");

  if (!raw) return;

  try {
    const data = JSON.parse(raw);

    // Si viene desde Servicios.jsx
    if (flujo === "servicio") {
      setPlanEntrada(null);
    } else if (data?.planId) {
      // 🔵 Cargar plan COMPLETO desde Firestore
      const ref = doc(db, "planes_usuarios", data.planId);
      getDoc(ref).then((snap) => {
      if (snap.exists()) {
        const fullPlan = { planId: snap.id, ...snap.data() };
        setPlanEntrada(fullPlan);

        // ✅ si el plan define modalidad, úsala en la UI (grupo/privada)
        if (fullPlan?.modalidad) {
          setModalidadKine(normModalidadUI(fullPlan.modalidad));
        }
      } else {
        setPlanEntrada(null);
      }
      });
    } else {
      setPlanEntrada(null);
    }


    if (typeof data?.categoria !== "string" || typeof data?.variante !== "string") {
      console.warn("Datos corruptos en localStorage, reseteando servicio.");
      localStorage.removeItem("servicioSeleccionado");
      localStorage.removeItem("flujoReserva");
      return;
    }

    // 1) Encontrar la categoría en el select de servicio
    const servicioOpt =
      opcionesServicios.find((o) =>
        o.value === data.categoria || o.label.includes(data.categoria)
      ) || null;

    // 2) Construir variantes para esa categoría
    const variantesParaServicio = SERVICIOS_DYNAMIC_FLAT
      .filter((s) => s.categoria === servicioOpt?.value)
      .map((s) => ({
        value: s.variante,
        label: s.display,
        data: s,
      }));

    // 3) Encontrar la variante
    const varianteOpt =
      variantesParaServicio.find(
        (v) =>
          v.value === data.variante || v.label.includes(data.variante)
      ) || null;

    setForm((f) => ({
      ...f,
      servicio: servicioOpt,
      variante: varianteOpt,
    }));
  } catch (err) {
    console.error("Error cargando datos:", err);
  }

  localStorage.removeItem("servicioSeleccionado");
  localStorage.removeItem("flujoReserva");
}, [opcionesServicios, servicios]);

// =====================
// Cargar configuración de métodos de pago
// =====================
useEffect(() => {
  const ref = doc(db, "config", "pagos");
  const unsub = onSnapshot(ref, (snap) => {
    if (snap.exists()) {
      setConfigPago(snap.data());
    } else {
      setConfigPago(null);
    }
  });
  return () => unsub();
}, []);



  // ===============================
  // FECHA ACTIVA
  // ===============================
  const activeDate = form.fecha;

  // ===============================
  // Campos obligatorios / Validación general
  // ===============================
  const horarioOK = !!primarySlot;
  const isOk = faltantes.length === 0 && horarioOK && !!usuario;

  // ===============================
  // Validar cupón
  // ===============================
  const validarCupon = async () => {
    if (!cupon.trim()) {
      setMensajeCupon("Ingresa un código de cupón.");
      setCuponInfo(null);
      return;
    }

    setCuponLoading(true);
    setMensajeCupon("");
    setCuponInfo(null);

    try {
      const q = query(
        collection(db, "cupones"),
        where("codigo", "==", cupon.trim().toUpperCase())
      );
      const snap = await getDocs(q);

      if (snap.empty) {
        setMensajeCupon("❌ Cupón inválido.");
        setCuponInfo(null);
        setCuponLoading(false);
        return;
      }

      const docSnap = snap.docs[0];
      const data = docSnap.data();
      const hoy = new Date().toISOString().slice(0, 10);
      // ===============================
      // Validar si aplica al servicio
      // ===============================

      // ===============================
      // Validar si aplica al servicio (por ID real de editar_servicios)
      // ===============================
      const tituloServicioUI =
        form?.servicio?.value || form?.servicio?.label || ""; // ej: "Sesiones de Kinesiología"

      const servicioIdActualRaw = getServicioIdDesdeTitulo(tituloServicioUI, servicios); // ej: "kinesiologia"
      const servicioIdActualNorm = normKey(servicioIdActualRaw);

      // compat extra: si por alguna razón el cupón se guardó con títulos (no ids), igual lo aceptamos
      const tituloNorm = normKey(tituloServicioUI);

      if (data.aplicacion === "uno" || data.aplicacion === "algunos") {
        const permitidos = (data.serviciosPermitidos || []).map(normKey);

       const permitido =
        (servicioIdActualNorm && permitidos.includes(servicioIdActualNorm)) ||
        permitidos.includes(tituloNorm);

        if (!permitido) {
          setMensajeCupon("❌ Este cupón no aplica a este servicio.");
          setCuponInfo(null);
          setCuponLoading(false);
          return;
        }
      }

      if (!data.activo) {
        setMensajeCupon("❌ Cupón inactivo.");
        setCuponInfo(null);
        setCuponLoading(false);
        return;
      }
      if (data.expira < hoy) {
        setMensajeCupon("❌ Cupón expirado.");
        setCuponInfo(null);
        setCuponLoading(false);
        return;
      }
      if (data.usos >= data.usosMax) {
        setMensajeCupon("❌ Este cupón ya alcanzó su límite de usos.");
        setCuponInfo(null);
        setCuponLoading(false);
        return;
      }

      // ===============================
      // Validar si aplica a la VARIANTE
      // ===============================
      const aplicacionVariantes = data.aplicacionVariantes || "todas";
      const variantesPermitidas = (data.variantesPermitidas || []).map(String);

      if (aplicacionVariantes === "algunas") {
        if (!servicioIdActualRaw) {
          setMensajeCupon("❌ No se pudo determinar el ID del servicio para validar la variante.");
          setCuponInfo(null);
          setCuponLoading(false);
          return;
        }

        const vData = form?.variante?.data || {};
        const varianteKey =
          vData?.id ||                 // si existe
          vData?.varianteKey ||        // si existe
          vData?.nombre ||             // lo más común en tu data
          vData?.variante ||           // fallback
          form?.variante?.value || ""; // último fallback

        const tokenSeleccionado = makeVarianteToken(servicioIdActualRaw, varianteKey);

        const okVariante = variantesPermitidas.includes(tokenSeleccionado);

        if (!okVariante) {
          setMensajeCupon("❌ Este cupón no aplica a esta variante.");
          setCuponInfo(null);
          setCuponLoading(false);
          return;
        }
      }

        const precioBase = getPrecioFinalAntesDescuento();

        let descuento = 0;
        if (data.tipo === "porcentaje") {
          descuento = Math.round(precioBase * (data.valor / 100));
        } else {
          descuento = Math.min(Number(data.valor || 0), precioBase);
        }

      setCuponInfo({
        ok: true,
        ref: docSnap.ref,
        data,
        descuento,
      });

      setMensajeCupon(`✅ Descuento aplicado: -${clp(descuento)}`);
    } catch (err) {
      console.error("Error validando cupón:", err);
      setMensajeCupon("⚠️ Error al validar el cupón.");
    }

    setCuponLoading(false);
  };

  const registrarUsoCupon = async () => {
    try {
      if (cuponInfo?.ok && cuponInfo?.ref) {
        await updateDoc(cuponInfo.ref, {
          usos: increment(1),
        });
      }
    } catch (err) {
      console.error("Error al incrementar usos del cupón:", err);
    }
  };

  const getPrecios = () => {
    const totalSesiones = sesionesPorServicioFromStrings(
      parsed.categoria,
      parsed.variante,
      meta
    );

    const esServicioConPlan = !!planEntrada || totalSesiones > 1 || parsed.esPlan;

    // =============================
    // CASO: viene de un plan activo
    // =============================
    if (planEntrada) {
      const precioSesionNormal =
        planEntrada.precio_por_sesion ||
        Math.round((planEntrada.precio_total || 0) / (planEntrada.sesiones_totales || 1));

      const precioPrimeraSesionPlan = esPrimeraSesionReal
        ? PRECIO_PRIMERA_SESION_PLAN
        : precioSesionNormal;

      return {
        precioOriginal: precioPrimeraSesionPlan,
        precioFinal: precioPrimeraSesionPlan,
        mitadFinal: precioPrimeraSesionPlan,
        precio_por_sesion: precioSesionNormal,
      };
    }

      const precioBase = getPrecioFinalAntesDescuento();

    // Aplicar cupón
    const descuento = cuponInfo?.ok ? cuponInfo.descuento : 0;
    const precioFinal = Math.max(0, precioBase - descuento);

    // Si es primera sesión de un servicio con plan/packs
    if (esServicioConPlan && esPrimeraSesionReal && totalSesiones > 1) {
      return {
        precioOriginal: precioBase,
        precioFinal,
        mitadFinal: PRECIO_PRIMERA_SESION_PLAN,
        precio_por_sesion: calcularPrecioSesionPosteriorPlan(precioFinal, totalSesiones),
      };
    }

    const mitadFinal =
      totalSesiones > 1
        ? Math.round(precioFinal / totalSesiones)
        : Math.round(precioFinal / 2);

    return {
      precioOriginal: precioBase,
      precioFinal,
      mitadFinal,
      precio_por_sesion: Math.round(precioFinal / totalSesiones),
    };
  };

  const { precioOriginal, precioFinal, mitadFinal, precio_por_sesion } = getPrecios();


  // ======================================================
  // BOTÓN DINÁMICO
  // ======================================================
  const abrirPago = () => {
    const esFonasa = /fonasa/i.test(parsed.variante);
    const vieneDesdePlan = Boolean(planEntrada);

    // 🔥 CORRECCIÓN:
    // NO pedir modalidad si viene desde un plan
    // NO pedir modalidad si es Fonasa
    // Mantener solo para Kine suelta (sin plan)
    if (
      esKine &&
      !esFonasa &&
      !vieneDesdePlan &&
      !esPrimeraSesionReal &&
      !modalidadKine
    ) {
      alert("Selecciona si tu sesión será grupal o privada.");
      return;
    }

    setModalAbierto(true);
    setPasoPago(1);
    setOpcionPago(null);
    setFormTransferencia({ comprobanteFile: null });
  };

const normTxt = (s) => (s || "").toString().trim().replace(/\s+/g, " ");

async function upsertUsuarioDesdeReserva({ usrRef, usuario, form, usrData }) {
  const email = (usuario?.email || form.correo || usrData?.correo || "").toLowerCase().trim();

  const nombresForm = normTxt(form.nombres);
  const apellidosForm = normTxt(form.apellidos);

  // ✅ No pisar si ya hay datos guardados
  const nombresFinal = normTxt(usrData?.nombres) || nombresForm || null;
  const apellidosFinal = normTxt(usrData?.apellidos) || apellidosForm || null;

  await setDoc(
    usrRef,
    {
      uid: usuario?.uid || usrData?.uid || null,
      correo: email || null,

      // ✅ JAMÁS concatenar acá
      nombres: nombresFinal,
      apellidos: apellidosFinal,

      // teléfonos
      telefono: form.celular || usrData?.telefono || usrData?.celular || null,
      celular: form.celular || usrData?.celular || null,
      t_emergencia:
        form.t_emergencia ||
        usrData?.t_emergencia ||
        usrData?.telefonoEmergencia ||
        null,

      actualizadoEn: serverTimestamp(),
      actualizadoDesde: "reserva",
    },
    { merge: true }
  );
}

      const crearReserva = async ({
        pago_tipo,
        monto_pagado,
        precio_total,
        desdePlan = false,
        planId = null,
        comprobanteURL = null,
        pago_metodo = null,
      }) => {
      // ============================
      // 1) Leer datos del usuario en Firestore
      // ============================
      const usrRef = doc(
        db,
        "usuarios",
        (usuario?.email || form.correo).toLowerCase()
      );
      const snapUsr = await getDoc(usrRef);
      const usrData = snapUsr.data() || {};

      // Mapa de primerasSesiones (estructura que muestras en Firebase)
      const primerasSesiones = usrData.primerasSesiones || {};

      // ============================
      // 2) Metadata de pareja (ya estaba)
      // ============================
      let parejaMeta = {};
      if (planEntrada?.es_pareja) {
        parejaMeta = {
          es_pareja: true,
          titular_uid: planEntrada.titular_uid || usuario.uid,
          acompanante_uid: planEntrada.acompanante_uid || null,
          acompanante_info: planEntrada.acompanante_info || null,
        };
      }

      // ============================
      // 3) DEFINICIÓN CORRECTA DE PRIMERA SESIÓN
      // ============================
      const clavePrimeraSesion = getClavePrimeraSesion(parsed.categoria, parsed.variante);

      let esPrimeraSesionReal = false;

      if (clavePrimeraSesion) {
        if (planEntrada) {
          esPrimeraSesionReal =
            (planEntrada.sesiones_realizadas ?? 0) === 0 &&
            (planEntrada.sesiones_reservadas ?? 0) === 0;
        } else {
          esPrimeraSesionReal = primerasSesiones[clavePrimeraSesion] !== true;
        }
      }



        const esFonasa = /fonasa/i.test(parsed.variante || "");

        // ✅ Base: si viene desde plan, manda el plan. Si no, manda lo que eligió el usuario
        let modalidadAgenda = null;

        if (desdePlan) {
          const planSnap = await getDoc(doc(db, "planes_usuarios", planId));

          modalidadAgenda = normModalidadDB(
            planSnap.data()?.modalidad
          );
        } else {
          modalidadAgenda = normModalidadDB(modalidadKine);
        }

        // Primera sesión siempre privada
        if (esPrimeraSesionReal) {
          modalidadAgenda = "privada";
        }


      // ============================
      // 4) Crear la reserva
      // ============================

      // ✅ Preparar fecha/hora finales (strings)
      const fechaStr = primarySlot?.fecha || form.fecha;
      const horaStr = primarySlot?.hora || null;

      if (!fechaStr || !horaStr) {
        throw new Error("Falta fecha u hora para crear la reserva.");
      }

      // ✅ Timestamp real de la sesión (CL). Usamos tu helper existente HHmmToDate()
      const startAt = Timestamp.fromDate(HHmmToDate(fechaStr, horaStr));

      // ✅ CUPÓN (snapshot para guardar en reserva)
      const cuponCodigo = cuponInfo?.ok
        ? (cuponInfo?.data?.codigo || cupon || "").toUpperCase().trim()
        : null;

      const cuponDescuento = cuponInfo?.ok ? Number(cuponInfo?.descuento || 0) : 0;

      const cuponId = cuponInfo?.ok ? (cuponInfo?.ref?.id || null) : null;


      const estadoInicial =
        desdePlan && (pago_tipo || "").toLowerCase() === "incluida"
          ? "aprobada"
          : "pendiente";

      const docRef = await addDoc(collection(db, "reservas"), {
        uid: usuario?.uid || null,
        email: (usuario?.email || form.correo || "").toLowerCase(),
        nombre: `${form.nombres || ""} ${form.apellidos || ""}`.trim(),
        telefono: form.celular || null,

        servicio_raw: `${parsed.categoria} - ${parsed.variante}`,
        servicio_categoria: parsed.categoria,
        servicio_variante: parsed.variante,

        modalidad: modalidadAgenda,
        esPareja: esEntrenamientoPareja,
        requiereSegundoParticipante: esEntrenamientoPareja,

        esPrimeraSesion: esPrimeraSesionReal,

        precio_total,
        monto_pagado,
        pago_tipo,
        pago_metodo,

        ...(cuponInfo?.ok
          ? {
              cupon_codigo: cuponCodigo,
              cupon_descuento: cuponDescuento,
              cupon_id: cuponId,
            }
          : {}),

        planId: planId || null,
        desdePlan,

        fecha: fechaStr,
        hora: horaStr,
        startAt,

        recordatorios: { h24_sentAt: null },

        comprobanteURL: comprobanteURL || null,

        estado: estadoInicial,

        estadoSesion: "pendiente",

        creadoEn: serverTimestamp(),

        ...parejaMeta,
      });


      // ============================
      // 5) Marcar primera sesión REAL del servicio
      // ============================
      const campo = getClavePrimeraSesion(parsed.categoria, parsed.variante);

      if (campo && esPrimeraSesionReal) {
        await updateDoc(usrRef, {
          [`primerasSesiones.${campo}`]: true,
        });
      }

      return docRef.id;
    };


  const actualizarPlan = async (planId, cambios) => {
    if (!planId) return;
    const ref = doc(db, "planes_usuarios", planId);
    await updateDoc(ref, cambios);
  };

const crearPlanNuevoSiEsPack = async () => {
  const totalSesiones = sesionesPorServicioFromStrings(
    parsed.categoria,
    parsed.variante,
    meta
  );

  if (totalSesiones <= 1) return null;

  const precioTotal = precioFinal;

  // 👇 Nombre "bonito" tal como aparece en el select
  const servicioLabel =
    form.servicio?.label || form.servicio?.value || parsed.categoria;

  const esPareja =
    parsed.categoria === "Entrenamiento" &&
    parsed.variante?.toLowerCase().includes("pareja");

  const modalidadPlan = normModalidadDB(modalidadKine);

  const planDoc = {
    uid: usuario.uid,
    titular_uid: usuario.uid,

    // ===== NUEVO: nombres coherentes con la UI =====
    servicio: servicioLabel,                     // "Entrenamiento en Pareja"
    servicio_categoria: parsed.categoria,        // "Entrenamiento" (normalizada)
    servicio_variante: parsed.variante,          // "2x (mensual) (Pareja)"
    servicio_raw: `${servicioLabel} - ${parsed.variante}`,
    modalidad: modalidadPlan,

    // Compatibilidad con código viejo
    categoria: servicioLabel,
    variante: parsed.variante,

    sesiones_totales: totalSesiones,
    sesiones_usadas: 0,
    sesiones_reservadas: 1,

    precio_total: precioTotal,
    precio_por_sesion:
      esPrimeraSesionReal && totalSesiones > 1
        ? calcularPrecioSesionPosteriorPlan(precioTotal, totalSesiones)
        : Math.round(precioTotal / totalSesiones),

    pago_total_realizado: 0,
    monto_restante: precioTotal,

    es_pareja: esPareja,
    pareja_completa: !esPareja,

    creadoEn: serverTimestamp(),
  };

  const docRef = await addDoc(collection(db, "planes_usuarios"), planDoc);
  return docRef.id;
};




  const reservarSesionIncluida = async () => {
    if (!planEntrada) return alert("No se detectó un plan activo.");

    try {
      const reservaId = await crearReserva({
        pago_tipo: "incluida",
        monto_pagado: 0,
        precio_total: planEntrada.precio_por_sesion,
        desdePlan: true,
        planId: planEntrada.planId,
      });

      await actualizarPlan(planEntrada.planId, {
        sesiones_reservadas: increment(1),
      });

      setResumenReserva({
nombre:
  usuario?.nombres && usuario?.apellidos
    ? `${usuario.nombres} ${usuario.apellidos}`
    : `${form.nombres || ""} ${form.apellidos || ""}`,

        correo: usuario.email,
        servicio: `${parsed.categoria}: ${parsed.variante}`,
        fecha: primarySlot.fecha,
        hora: primarySlot.hora,
        monto: "$0 (incluida)",
      });

      setMostrarConfirmacion(true);
    } catch (err) {
      console.error(err);
      alert("Error al reservar la sesión incluida.");
    }
  };

  const registrarPagoYReserva = async ({
    monto_pagado,
    comprobanteURL = null,
    pago_metodo,
  }) => {
    try {
      let planId = planEntrada?.planId || null;
      let esPlanNuevo = false;

      // =====================================
      // 1) Si NO hay plan activo, ver si es pack nuevo
      // =====================================
      if (!planEntrada) {
        const nuevoPlanId = await crearPlanNuevoSiEsPack();
        if (nuevoPlanId) {
          planId = nuevoPlanId;
          esPlanNuevo = true;
        }
      }

      // =====================================
      // 2) Calcular pago_tipo_real y precio_total_ref
      //    (lo que se guarda en la reserva)
      // =====================================
      let pago_tipo_real = "completo";
      let precio_total_ref = 0;

      if (planEntrada) {
        // ----- CASO: viene de un plan -----
        const valorSesion =
          planEntrada.precio_por_sesion ||
          Math.round(
            (planEntrada.precio_total || 0) /
              (planEntrada.sesiones_totales || 1)
          );

        // Para planes, el modal guarda en form.pagoSeleccionado
        const pagoSeleccionado = form.pagoSeleccionado || opcionPago;

        if (pagoSeleccionado === "plan_saldo") {
          // Está pagando TODO el saldo pendiente del plan
          pago_tipo_real = "pago_total_restante";
          // precio_total_ref = lo que realmente estaba pendiente
          precio_total_ref =
            planEntrada.monto_restante || monto_pagado || 0;
        } else if (pagoSeleccionado === "plan_sesion") {
          // Está pagando SOLO la próxima sesión
          pago_tipo_real = "pago_proxima";
          precio_total_ref = valorSesion;
        } else if ((planEntrada.monto_restante || 0) === 0) {
          // Plan ya pagado al 100% → sesión incluida
          pago_tipo_real = "incluida";
          precio_total_ref = valorSesion;
        } else {
          // Fallback por si algo raro pasa
          pago_tipo_real = planEntrada.pago_tipo || "pago_proxima";
          precio_total_ref = valorSesion;
        }
      } else {

      const precioBase = getPrecioFinalAntesDescuento();

      const descuento = cuponInfo?.ok ? cuponInfo.descuento : 0;
      const precioFinalServicio = Math.max(0, precioBase - descuento);

      const totalSesiones = sesionesPorServicioFromStrings(
        parsed.categoria,
        parsed.variante,
        meta
      );

        // Detectamos pago inicial por monto, NO por opcionPago
        if (totalSesiones === 1 && monto_pagado === mitadFinal) {
            pago_tipo_real = "reserva50";
        }
        else if (totalSesiones > 1) {
          // SIEMPRE que sea plan → el primer pago es inicial
          if (monto_pagado === mitadFinal) {
              pago_tipo_real = "pago_inicial_plan";
          } else if (monto_pagado >= precioFinal) {
              pago_tipo_real = "completo";
          }
      } else {
          // Servicios normales de 1 sesión
          if (monto_pagado === mitadFinal) {
              pago_tipo_real = "reserva50";
          } else {
              pago_tipo_real = "completo";
          }
      }
      precio_total_ref = precioFinalServicio;
    }


      // =====================================
      // 3) Crear la reserva con datos coherentes
      // =====================================
      const reservaId = await crearReserva({
        pago_tipo: pago_tipo_real,
        monto_pagado,
        precio_total: precio_total_ref,
        desdePlan: !!planId,
        planId,
        comprobanteURL,
        pago_metodo,
      });

      // =====================================
      // 4) Actualizar PLAN EXISTENTE (planEntrada)
      // =====================================
      if (planEntrada && planEntrada.planId) {
        if (pago_tipo_real === "pago_proxima") {
          // Pagó una sesión del plan
          await actualizarPlan(planEntrada.planId, {
            sesiones_reservadas: increment(1),
            pago_total_realizado: increment(monto_pagado),
            monto_restante: increment(-monto_pagado),
          });
        } else if (pago_tipo_real === "pago_total_restante") {
          // Pagó todo el saldo pendiente
          await actualizarPlan(planEntrada.planId, {
            sesiones_reservadas: increment(1),
            pago_total_realizado: increment(monto_pagado),
            monto_restante: 0,
          });
        } else if (pago_tipo_real === "incluida") {
          // Sesión incluida, no suma pago pero sí cuenta reserva
          await actualizarPlan(planEntrada.planId, {
            sesiones_reservadas: increment(1),
          });
        }
      }

      // =====================================
      // 5) Actualizar PLAN NUEVO (cuando se crea plan por pack)
      // =====================================
      if (esPlanNuevo && planId) {
        await actualizarPlan(planId, {
          pago_total_realizado: increment(monto_pagado),
          monto_restante: increment(-monto_pagado),
        });
      }

      // =====================================
      // 6) Registrar cupón (si aplica)
      // =====================================
      if (cuponInfo?.ok) {
        await registrarUsoCupon();
      }

      // =====================================
      // 7) Resumen para el modal
      // =====================================
      setResumenReserva({
              nombre:
        usuario?.nombre && usuario?.apellido
          ? `${usuario.nombre} ${usuario.apellido}`
          : `${form.nombres || ""} ${form.apellidos || ""}`,
        correo: usuario.email,
        telefono: form.celular || usuario?.telefono || null,
        servicio: `${parsed.categoria}: ${parsed.variante}`,
        fecha: primarySlot.fecha,
        hora: primarySlot.hora,
        monto: clp(monto_pagado),
      });

      if (
        parsed.categoria === "Entrenamiento" &&
        parsed.variante.toLowerCase().includes("pareja")
      ) {
        window.location.href = `/paciente/completar-pareja/${planId}`;
        return;
      }


      setMostrarConfirmacion(true);
    } catch (err) {
      console.error(err);
      alert("Error al registrar el pago y la reserva.");
    }
  };


  const pagarPorTransferencia = async () => {
    if (!formTransferencia.comprobanteFile) {
      alert("Selecciona una imagen del comprobante.");
      return;
    }

    setEnviando(true);

    try {
      const { ref, uploadBytes, getDownloadURL } = await import("firebase/storage");
      const { storage } = await import("../../firebase/config.js");

      const fileRef = ref(
        storage,
        `comprobantes/${Date.now()}_${formTransferencia.comprobanteFile.name}`
      );
      await uploadBytes(fileRef, formTransferencia.comprobanteFile);
      const url = await getDownloadURL(fileRef);

      // ⬅️ AHORA EL MONTO SE TOMA DEL CÁLCULO UNIFICADO
      const montoAPagar = montoAPagarActual;

      await registrarPagoYReserva({
        monto_pagado: montoAPagar,
        comprobanteURL: url,
        pago_metodo: "transferencia",
      });
    } catch (err) {
      console.error(err);
      alert("Error al subir el comprobante.");
    } finally {
      setEnviando(false);
    }
  };


  const copiarDatosTransferencia = async () => {
    const montoTexto = clp(
      form.montoAPagarActual ||
        (planEntrada
          ? montoAPagarActual
          : opcionPago === "half"
          ? mitadFinal
          : precioFinal)
    );


    const texto = [
      `Servicio: ${parsed.categoria}: ${parsed.variante}`,
      `Monto: ${montoTexto}`,
      `Nombre: Paula Montes Walker`,
      `RUT: 19.513.772-2`,
      `Banco/App: Mercado Pago`,
      `Tipo de cuenta: Cuenta Vista`,
      `N° de cuenta: 1039441329`,
      `Email: paulamontesw@gmail.com`,
    ].join("\n");

    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 1500);
    } catch {
      alert("No se pudo copiar. Copia manualmente.");
    }
  };

  // cálculo final del monto según el estado elegido
const montoFinalMostrar = 
  montoAPagarActual ??
  (planEntrada
    ? opcionPago === "plan_saldo"
      ? planEntrada.monto_restante
      : opcionPago === "plan_sesion"
      ? planEntrada.precio_por_sesion
      : planEntrada.monto_restante === 0
      ? 0
      : planEntrada.precio_por_sesion
    : opcionPago === "half"
    ? mitadFinal
    : precioFinal);


let esUltima = false;

if (planEntrada) {
  const reservasPlan = JSON.parse(localStorage.getItem("reservasPlan") || "[]");

  const sesionesValidas = reservasPlan.filter(estadoCuentaParaProgreso);

  const realizadas = sesionesValidas.filter(cuentaComoRealizada).length;
  const activas = sesionesValidas.filter(r =>
    ["pendiente", "aprobada"].includes(r.estado)
  ).length;

  const sesionActual = realizadas + activas + 1;
  const total = planEntrada.sesiones_totales;

  esUltima = sesionActual === total;
}


  // ======================================================
  // RENDER PRINCIPAL
  // ======================================================
  return (
    <>
      <div className="min-h-screen bg-[radial-gradient(80rem_40rem_at_100%_0%,rgba(58,106,255,0.10),transparent),radial-gradient(60rem_40rem_at_0%_100%,rgba(0,0,0,0.05),transparent)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="text-center mb-8">
            <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-[#0E1A36]">
              Reserva tu Sesión
            </h1>
            <p className="text-gray-600 mt-2">
              Completa los pasos y confirma tu horario.
            </p>
          </div>

          {/* PASOS */}
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mb-10">
            <Paso n={1} label="Tus datos" activo={!usuario && mostrarDatos} completo={!!usuario || !mostrarDatos} />
            <span className="hidden sm:block text-gray-300">—</span>
            <Paso n={2} label="Servicio" activo={!form.servicio} completo={!!form.servicio} />
            <span className="hidden sm:block text-gray-300">—</span>
            <Paso n={3} label="Fecha y hora" activo={!!form.servicio && !primarySlot} completo={!!primarySlot} />
            <span className="hidden sm:block text-gray-300">—</span>
            <Paso n={4} label="Confirmar" activo={isOk} completo={isOk} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* COLUMNA IZQUIERDA */}
            <div className="lg:col-span-2 space-y-6">
              <DatosPaciente
                usuario={usuario}
                form={form}
                setForm={setForm}
                mostrarDatos={mostrarDatos}
                setMostrarDatos={setMostrarDatos}
                handleChange={handleChange}
                comunasFiltradas={comunasFiltradas}
                regionesYComunas={regionesYComunas}
                crearCuenta={crearCuenta}
                handleGoogleSignup={handleGoogleSignup}
              />
              
                <ServicioSelector
                  form={form}
                  setForm={setForm}
                  planEntrada={planEntrada}
                  opcionesServicios={opcionesServiciosAgrupadas}
                  opcionesVariantes={opcionesVariantes}
                  selectStyles={selectStyles}

                  // datos especiales que ya existen en agenda.jsx
                  esKine1Sesion={esKine1Sesion}
                  preciosKine1={preciosKine1}
                  modalidadKine={modalidadKine}
                  setModalidadKine={setModalidadKine}

                  servicios={servicios}
                  parsed={parsed}
                  totalSesionesEstimadas={totalSesionesEstimadas}
                  sesionesPorServicioFromStrings={sesionesPorServicioFromStrings}
                  clp={clp}

                  usuario={usuario}

                  setPlanEntrada={setPlanEntrada}
                  preseleccionarServicioDesdePlan={preseleccionarServicioDesdePlan}
                  normModalidadUI={normModalidadUI}
                  setPrimarySlot={setPrimarySlot}
                />


              <FechaSelector
                form={form}
                setForm={setForm}
                minDate={minDate}
                minDateForService={minDateForService}
                fechaError={fechaError}
                primarySlot={primarySlot}
                setPrimarySlot={setPrimarySlot}
              />

              <HorariosGrid
                form={form}
                setForm={setForm}

                parsed={parsed}
                horarios={horarios}
                fechaError={fechaError}

                isMasajesMitadSemana={isMasajesMitadSemana}
                isMasoterapia={isMasoterapia}
                isDiaPermitidoMasoterapia={isDiaPermitidoMasoterapia}

                minDateForService={minDateForService}
                minDate={minDate}

                activeDate={activeDate}
                primarySlot={primarySlot}
                onPickPrimary={onPickPrimary}
                reglaServicio={reglaActual}  

                /* 👇 ESTO FALTABA */
                estadoPorHora={estadoPorHora}
                ocupacionBloques={ocupacionBloques}
                contadorPorSlot={contadorPorSlot}
                effectiveBreaks={effectiveBreaks}
                modalidadKine={modalidadKine}

                esEntrenamiento={parsed?.categoria === "Entrenamiento"}
                esKine1Sesion={esKine1Sesion}

                categoria={parsed?.categoria}
                duracionSesion={duracionSesion}
              />

              {/* Botón dinámico (desktop) */}
            </div>

            <ResumenReserva
              parsed={parsed}
              planEntrada={planEntrada}
              precioOriginal={precioOriginal}
              precioFinal={precioFinal}
              mitadFinal={mitadFinal}
              reservasPlan={JSON.parse(localStorage.getItem("reservasPlan") || "[]")}
              sesionesTotales={planEntrada?.sesiones_totales || null}
              cupon={cupon}
              setCupon={setCupon}
              cuponInfo={cuponInfo}
              cuponLoading={cuponLoading}
              mensajeCupon={mensajeCupon}
              validarCupon={validarCupon}
              totalSesionesEstimadas={totalSesionesEstimadas}
              primarySlot={primarySlot}
              aceptaTerminos={aceptaTerminos}
              setAceptaTerminos={setAceptaTerminos}
              isOk={isOk}
              usuario={usuario}
              reservarSesionIncluida={reservarSesionIncluida}
              abrirPago={abrirPago}
            />

          </div>
        </div>
      </div>

      {modalAbierto && (
        <ModalPago
          pasoPago={pasoPago}
          setPasoPago={setPasoPago}
          setModalAbierto={setModalAbierto}

          usuario={usuario}
          form={form}

          parsed={parsed}
          totalSesionesEstimadas={totalSesionesEstimadas}
          esPrimeraSesionReal={esPrimeraSesionReal}
          modalidadKine={modalidadKine}

          precioFinal={precioFinal}
          mitadFinal={mitadFinal}

          montoAPagarActual={montoAPagarActual}
          setMontoAPagarActual={setMontoAPagarActual}

          planEntrada={planEntrada}
          esUltima={esUltima}

          opcionPago={opcionPago}
          setOpcionPago={setOpcionPago}

          registrarPagoYReserva={registrarPagoYReserva}
          pagarPorTransferencia={pagarPorTransferencia}
          copiarDatosTransferencia={copiarDatosTransferencia}

          formTransferencia={formTransferencia}
          setFormTransferencia={setFormTransferencia}
          enviando={enviando}

          primarySlot={primarySlot}
          configPago={configPago}
        />
      )}

      {mostrarConfirmacion && (
        <ModalConfirmacion
          resumenReserva={resumenReserva}
          onClose={() => {
            setMostrarConfirmacion(false);
            window.location.href = "/paciente/sesiones";
          }}
        />
      )}
    </>
  );
};

export default Agenda;